import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Radio, Button, Typography, Breadcrumb, Row, Col, Select, Spin, DatePicker } from 'antd';
import { InfoCircleOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { CreateGreetingRequest, GreetingItem } from '../types/greeting';
import greetingService from '../services/greeting';
import { message } from '../utils/message';
import { showConfirmModal } from '../components/PopupModal';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';
import { useNavigationBlockerContext } from '../contexts/NavigationBlockerContext';
import api from '../services/api';
import GreetingPreviewModal from '../components/GreetingPreviewModal';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const GreetingAdd: React.FC = () => {
  const navigate = useNavigate();
  const { clearAllBlockers } = useNavigationBlockerContext();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingLayouts, setLoadingLayouts] = useState(true);
  const [layouts, setLayouts] = useState<string[]>([]);
  const [isModified, setIsModified] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);
  const [contentList, setContentList] = useState<string[]>(['']);  // 내용 배열 (최대 2개)
  const [previewVisible, setPreviewVisible] = useState(false);  // 미리보기 모달
  const [previewGreeting, setPreviewGreeting] = useState<GreetingItem | null>(null);  // 미리보기용 greeting 데이터
  useNavigationBlocker(isModified, '/greeting-management');

  // 레이아웃 목록 및 입주사명 목록 조회
  useEffect(() => {
    const fetchLayouts = async () => {
      try {
        setLoadingLayouts(true);
        const layoutList = await greetingService.getLayouts();
        setLayouts(layoutList);
      } catch (error) {
        console.error('레이아웃 목록 조회 실패:', error);
        message.error('레이아웃 목록을 불러오는데 실패했습니다.');
        // 기본 레이아웃 사용
        setLayouts([
          '-'
        ]);
      } finally {
        setLoadingLayouts(false);
      }
    };

    const loadCompanyNames = async () => {
      try {
        const response = await api.get('/tenant-units');
        const data = response.data;

        if (data.success && data.data) {
          // 모든 층의 rooms에서 companyName 추출
          const companyNames = new Set<string>();
          data.data.forEach((floor: any) => {
            floor.rooms.forEach((room: any) => {
              if (room.companyName && room.companyName.trim() !== '') {
                companyNames.add(room.companyName);
              }
            });
          });

          const options = Array.from(companyNames)
            .sort()
            .map(name => ({
              value: name,
              label: name,
            }));

          setCompanyOptions(options);
        }
      } catch (error) {
        console.error('Failed to load company names:', error);
      }
    };

    fetchLayouts();
    loadCompanyNames();
  }, []);

  // 레이아웃 로드 후 첫 번째 레이아웃 자동 선택
  useEffect(() => {
    if (layouts.length > 0) {
      const firstLayout = layouts[0];
      form.setFieldValue('layoutName', firstLayout);
    }
  }, [layouts, form]);

  // 내용 추가 핸들러
  const handleAddContent = () => {
    if (contentList.length < 2) {
      setContentList([...contentList, '']);
      setIsModified(true);
    }
  };

  // 내용 삭제 핸들러
  const handleRemoveContent = (index: number) => {
    const newList = contentList.filter((_, i) => i !== index);
    setContentList(newList.length > 0 ? newList : ['']);
    setIsModified(true);
  };

  // 내용 변경 핸들러
  const handleContentChange = (index: number, value: string) => {
    const newList = [...contentList];
    newList[index] = value;
    setContentList(newList);
    setIsModified(true);
  };

  // 미리보기 핸들러
  const handlePreview = () => {
    const formValues = form.getFieldsValue();
    const previewGreeting: GreetingItem = {
      id: 0,
      layoutName: formValues.layoutName || '',
      exposureType: formValues.exposureType || 'show',
      startDate: formValues.startDate || null,
      endDate: formValues.endDate || null,
      startTime: formValues.startTime || null,
      endTime: formValues.endTime || null,
      title: formValues.title || '',
      visitorCompanyName: formValues.visitorCompanyName || null,
      content: contentList.filter(c => c.trim() !== '').length > 0
        ? contentList.filter(c => c.trim() !== '')
        : [''],
      createdAt: new Date().toISOString(),
      createdBy: 0
    };
    setPreviewVisible(true);
    // 미리보기를 위한 임시 greeting 객체를 state로 관리
    setPreviewGreeting(previewGreeting);
  };

  // 폼 제출 핸들러
  const handleSubmit = async (values: any) => {
    // 내용 검증
    const validContents = contentList.filter(c => c.trim() !== '');
    if (validContents.length === 0) {
      message.error('내용을 최소 1개 이상 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      // 비노출일 경우 시간을 null로 설정 (분은 00으로 고정)
      const startTime = values.exposureType === 'hide'
        ? null
        : (values.startHour ? `${values.startHour}:00:00` : null);
      const endTime = values.exposureType === 'hide'
        ? null
        : (values.endHour ? `${values.endHour}:00:00` : null);

      const formData: CreateGreetingRequest = {
        layoutName: values.layoutName,
        exposureType: values.exposureType,
        startDate: values.exposureType === 'hide'
          ? null
          : (values.exposurePeriod && values.exposurePeriod[0] ? values.exposurePeriod[0].format('YYYY-MM-DD') : null),
        endDate: values.exposureType === 'hide'
          ? null
          : (values.exposurePeriod && values.exposurePeriod[1] ? values.exposurePeriod[1].format('YYYY-MM-DD') : null),
        startTime,
        endTime,
        title: values.title,
        visitorCompanyName: values.visitorCompanyName || null,
        content: validContents,
      };

      await greetingService.createGreeting(formData);
      message.success('성공적으로 추가되었습니다.');
      clearAllBlockers();
      navigate('/greeting-management');

    } catch (error) {
      message.error('그리팅 추가 중 오류가 발생했습니다.');
      console.error('Create greeting error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 취소 핸들러 (네비게이션 경고 포함)
  const handleCancel = () => {
    if (isModified) {
      showConfirmModal({
        title: '알림',
        content: '작성한 내용은 복구할 수 없습니다.\n이전 단계로 이동하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => {
          setIsModified(false);
          setTimeout(() => {
            navigate('/greeting-management');
          }, 0);
        },
      });
    } else {
      navigate('/greeting-management');
    }
  };

  if (loadingLayouts) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px', color: '#ffffff' }}>
          데이터를 불러오는 중...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        style={{ marginBottom: '24px' }}
        items={[
          { title: '콘텐츠 관리' },
          { title: '미디어 아트' },
          { title: '그리팅', href: '/greeting-management' },
          { title: '그리팅 추가' },
        ]}
      />

      {/* 페이지 헤더 */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Title level={2} style={{ color: '#ffffff', margin: 0 }}>
            그리팅 추가
          </Title>
        </div>
        <Button onClick={handlePreview}>
          미리보기
        </Button>
      </div>

      {/* 메인 폼 */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={() => message.error('필수 입력 항목을 확인해주세요.')}
        requiredMark={false}
        onValuesChange={() => setIsModified(true)}
        initialValues={{
          exposureType: 'show',
          alwaysVisible: false,
          exposurePeriod: [dayjs(), dayjs().add(1, 'day')],
          startHour: '00',
          endHour: '00',
        }}
      >
        {/* 기본 정보 섹션 */}
        <Card
          title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>기본 정보</Text>}
          style={{
            marginBottom: '24px',
            background: '#1f1f1f',
            border: '1px solid #303030',
          }}
          styles={{ body: { padding: '24px' } }}
        >
          <Row gutter={[24, 16]}>
            <Col span={9}>
              <Form.Item
                label={<Text style={{ color: '#ffffff' }}>레이아웃명 <span style={{ color: 'red' }}>*</span></Text>}
                name="layoutName"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(new Error('레이아웃명을 선택해주세요.'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  placeholder="레이아웃을 선택하세요"
                  disabled
                  style={{ background: '#141414', border: '0px solid #303030' }}
                  styles={{
                    popup: {
                      root: {
                        background: '#1f1f1f'
                      }
                    }
                  }}
                >
                  {layouts.map((layout) => (
                    <Option key={layout} value={layout}>
                      {layout}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={<Text style={{ color: '#ffffff' }}>노출여부 <span style={{ color: 'red' }}>*</span></Text>}
                name="exposureType"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(new Error('노출여부를 선택해주세요.'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value="show" style={{ color: '#ffffff' }}>노출</Radio>
                  <Radio value="hide" style={{ color: '#ffffff' }}>비노출</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col span={9}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.exposureType !== currentValues.exposureType
                }
              >
                {({ getFieldValue }) => {
                  const exposureType = getFieldValue('exposureType');
                  const isExposureShow = exposureType === 'show';
                  const isDatePickerDisabled = !isExposureShow;

                  return (
                    <>
                      <Form.Item
                        label={
                          <Text style={{ color: '#ffffff' }}>
                            노출 기간 {isExposureShow && <span style={{ color: 'red' }}>*</span>}
                          </Text>
                        }
                        name="exposurePeriod"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (isExposureShow && (!value || !value[0] || !value[1])) {
                                return Promise.reject(new Error('노출 기간을 선택해주세요.'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <RangePicker
                          format="YYYY.MM.DD"
                          disabled={isDatePickerDisabled}
                          style={{
                            width: '100%',
                            background: isDatePickerDisabled ? '#0a0a0a' : '#141414',
                            border: '1px solid #303030',
                            opacity: isDatePickerDisabled ? 0.5 : 1,
                            cursor: isDatePickerDisabled ? 'not-allowed' : 'pointer'
                          }}
                        />
                      </Form.Item>
                    </>
                  );
                }}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.exposureType !== currentValues.exposureType
                }
              >
                {({ getFieldValue }) => {
                  const exposureType = getFieldValue('exposureType');
                  const isExposureShow = exposureType === 'show';
                  const isTimePickerDisabled = !isExposureShow;

                  return (
                    <Form.Item
                      label={<Text style={{ color: '#ffffff' }}></Text>}
                      style={{ marginBottom: 0 }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0px',
                        marginTop: '-9px',
                        marginLeft: '-16px',
                      }}>
                        <Form.Item
                          name="startHour"
                          noStyle
                        >
                          <Select
                            disabled={isTimePickerDisabled}
                            allowClear={false}
                            style={{
                              width: '90px',
                              background: isTimePickerDisabled ? '#0a0a0a' : '#141414',
                              border: '0px solid #303030',
                              borderRadius: '6px',
                              outline: 'none',
                              boxShadow: 'none',
                              opacity: isTimePickerDisabled ? 0.5 : 1,
                              cursor: isTimePickerDisabled ? 'not-allowed' : 'pointer'
                            }}
                            suffixIcon={<span style={{ color: isTimePickerDisabled ? '#666666' : '#ffffff' }}>▼</span>}
                            styles={{
                              popup: {
                                root: {
                                  background: '#1f1f1f'
                                }
                              }
                            }}
                            className="custom-time-select"
                          >
                            {Array.from({ length: 24 }, (_, i) => (
                              <Option key={i} value={String(i).padStart(2, '0')}>
                                {String(i).padStart(2, '0')}시
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Text style={{ color: isTimePickerDisabled ? '#666666' : '#ffffff', margin: '0 8px' }}>~</Text>
                        <Form.Item
                          name="endHour"
                          noStyle
                        >
                          <Select
                            disabled={isTimePickerDisabled}
                            allowClear={false}
                            style={{
                              width: '90px',
                              background: isTimePickerDisabled ? '#0a0a0a' : '#141414',
                              border: '0px solid #303030',
                              borderRadius: '6px',
                              outline: 'none',
                              boxShadow: 'none',
                              opacity: isTimePickerDisabled ? 0.5 : 1,
                              cursor: isTimePickerDisabled ? 'not-allowed' : 'pointer'
                            }}
                            suffixIcon={<span style={{ color: isTimePickerDisabled ? '#666666' : '#ffffff' }}>▼</span>}
                            styles={{
                              popup: {
                                root: {
                                  background: '#1f1f1f'
                                }
                              }
                            }}
                            className="custom-time-select"
                          >
                            {Array.from({ length: 24 }, (_, i) => (
                              <Option key={i} value={String(i).padStart(2, '0')}>
                                {String(i).padStart(2, '0')}시
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '-12px', marginLeft: '16px' }}>
              <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
              <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                노출 기간 동안 기존 콘텐츠보다 상위에 오버레이되어 노출됩니다.<br />기간이 종료되면 자동 비공개 처리됩니다.
              </Text>
            </div>
          </Row>
        </Card>

        {/* 그리팅 정보 섹션 */}
        <Card
          title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>그리팅 정보</Text>}
          style={{
            marginBottom: '24px',
            background: '#1f1f1f',
            border: '0px solid #303030',
          }}
          styles={{ body: { padding: '24px' } }}
        >
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <Form.Item
                label={<Text style={{ color: '#ffffff' }}>제목 <span style={{ color: 'red' }}>*</span></Text>}
                name="title"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.reject(new Error('제목을 입력해주세요.'));
                      }
                      if (value.length > 100) {
                        return Promise.reject(new Error('제목은 100자 이하로 입력해주세요.'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="그리팅 제목을 입력하세요"
                  maxLength={100}
                  showCount
                  style={{ background: '#141414', border: '1px solid #303030', color: '#ffffff' }}
                />
              </Form.Item>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '-12px' }}>
                <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                  제목은 디스플레이에 노출되지 않습니다.
                </Text>
              </div>
            </Col>
          </Row>
          <Row gutter={[24, 16]} align="middle" style={{ marginTop: '16px' }}>
            <Col span={8}>
              <Form.Item
                label={<Text style={{ color: '#ffffff' }}>방문 입주사명 <span style={{ color: 'red' }}>*</span></Text>}
                name="visitorCompanyName"
                rules={[{ required: true, message: '방문 입주사명을 선택해주세요.' }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  showSearch
                  placeholder="회사명을 입력하세요"
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={companyOptions}
                  style={{ width: '100%' }}
                  notFoundContent="등록된 입주사가 없습니다."
                />
              </Form.Item>
            </Col>
            <Col>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '22px' }}>
                <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                  입주사 관리에 등록된 입주사명만 검색됩니다.
                </Text>
              </div>
            </Col>
          </Row>
          <Row gutter={[24, 16]} style={{ marginTop: '16px' }}>
            <Col span={8}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Text style={{ color: '#ffffff' }}>
                    내용 <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                    <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                      내용은 최대 2개까지 작성 가능합니다.
                    </Text>
                  </div>
                </div>
                {contentList.map((content, index) => (
                  <div key={index} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <Input
                          placeholder={`내용 ${index + 1}을 입력하세요 (최대 18자)`}
                          value={content}
                          onChange={(e) => handleContentChange(index, e.target.value)}
                          maxLength={18}
                          style={{
                            background: '#141414',
                            border: '1px solid #303030',
                            color: '#ffffff',
                            paddingRight: '50px',
                          }}
                        />
                        <Text style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: '12px',
                          color: '#a6a6a6'
                        }}>
                          {content.length}/18
                        </Text>
                      </div>
                      {contentList.length > 1 && (
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          onClick={() => handleRemoveContent(index)}
                          style={{ color: '#a6a6a6' }}
                        />
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddContent}
                  disabled={contentList.length >= 2}
                  style={{
                    width: '132px',
                    height: '42px',
                    marginTop: '8px',
                    background: 'transparent',
                    border: '1px dashed #303030',
                    color: contentList.length >= 2 ? '#666666' : '#ffffff'
                  }}
                >
                  내용추가
                </Button>
                {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                  <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                    내용은 최대 2개까지 작성 가능하며, 각 내용은 최대 18자까지 입력 가능합니다.
                  </Text>
                </div> */}
              </div>
            </Col>
          </Row>
        </Card>

        {/* 액션 버튼 */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '32px'
        }}>
          <Button
            size="large"
            onClick={handleCancel}
            style={{
              minWidth: '100px',
              background: 'transparent',
              border: '1px solid #303030',
              color: '#ffffff'
            }}
          >
            취소
          </Button>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={loading}
            style={{
              minWidth: '100px',
              background: '#F24822',
              border: 'none'
            }}
          >
            추가
          </Button>
        </div>
      </Form>

      {/* 미리보기 모달 */}
      {previewGreeting && (
        <GreetingPreviewModal
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          greeting={previewGreeting}
        />
      )}
    </div>
  );
};

export default GreetingAdd;